import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import ReplayRecording from "./components/ReplayRecording";
import { AuthProvider, useAuth } from "./components/AuthContext";
import Login from "./components/Login";
import AuthCallback from "./components/AuthCallback";
import Dashboard from "./components/Dashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import TryItOut from "./components/documentation/TryItOut";
import ViewRecordings from "./components/ViewRecordings";
import MfaAuthenticator from "./components/MfaAuthenticator";
import CommureLogo from "./commure.svg";
import CookieManager from "./components/CookieManager";
import SchedulerTriggers from "./components/SchedulerTriggers";
import { useLocation } from 'react-router'


const MethodBadge = ({ method }) => {
  const colors = {
    GET: "bg-green-100 text-green-700",
    POST: "bg-blue-100 text-blue-700",
    PUT: "bg-yellow-100 text-yellow-700",
    DELETE: "bg-red-100 text-red-700",
    FUNCTION: "bg-purple-100 text-purple-700",
  };

  return (
    <span
      className={`inline-block px-2 py-1 text-sm font-medium rounded-md ${
        colors[method] || "bg-gray-100 text-gray-700"
      }`}
    >
      {method}
    </span>
  );
};

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
    {children}
  </div>
);

const Button = ({ children, onClick, className = "" }) => (
  <button
    className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors ${className}`}
    onClick={onClick}
  >
    {children}
  </button>
);

const apiDocs = [
  {
    title: "Authentication",
    description: "All API endpoints require authentication using a Bearer token.",
    method: "AUTH",
    endpoint: "Authorization: Bearer <token>",
    params: [],
    additionalInfo: [
      {
        title: "Getting Started",
        items: [
          "All requests must include an Authorization header with a Bearer token",
          "Contact Sai or Ash to obtain your Bearer token",
          "Example: Authorization: Bearer your_token_here",
          "Keep your token secure and do not share it with others"
        ]
      },
      {
        title: "Example Request with Authentication",
        items: [
          "curl -X POST https://errands.fdebackend.com/scout/ \\",
          "  -H 'Authorization: Bearer your_token_here' \\",
          "  -H 'Content-Type: application/json' \\",
          "  -d '{\"user_prompt\": \"example prompt\"}'"
        ]
      }
    ]
  },
  {
    title: "Create Scout Session",
    endpoint: "/scout/",
    method: "POST",
    description: "Creates a new Commure Scout session with a user prompt.",
    authentication: "Bearer token required",
    params: [
      { 
        name: "user_prompt", 
        type: "string", 
        required: true, 
        description: "The prompt or question from the user" 
      },
      {
        name: "operator_controller_type",
        type: "string",
        required: false,
        description: "The type of Scout controller to use. Defaults to None. Only other option is 'RCM_OPS_TWO_FACTOR_AUTH' for RCM Ops two factor auth workflow."
      },
      {
        name: "temperature",
        type: "float",
        required: false,
        description: "Temperature to control the randomness of the AI. 0 means less random more deterministic. 2 means random and unpredictable. Default is 0.2."
      },
      {
        name: "cookie_name",
        type: "string",
        required: false,
        description: "Name of the stored cookie configuration to use for the session"
      },
      {
        name: "deposit_cookies",
        type: "boolean",
        required: false,
        description: "Whether to save the cookies after the session completes. Default is false."
      },
      {
        name: "advanced_browser_settings",
        type: "boolean",
        required: false,
        description: "Enable advanced browser settings for better stealth and ad blocking. Default is false."
      }      
    ],
    responseExample: {
      id: 1,
      user_prompt: "Go to google.com and search for the weather in San Francisco",
      status: "QUEUED",
      operator_response: null,
      comments: null,
      created_at: "2024-03-15T10:30:00Z"
    }
  },
  {
    title: "Execute Custom Script",
    endpoint: "/scout/execute-script",
    method: "POST",
    description: "Execute a custom Playwright script using Browserbase.",
    authentication: "Bearer token required",
    params: [
      {
        name: "script",
        type: "string",
        required: true,
        description: "The Playwright script to execute. Must be valid Python code using Playwright. The playwright page object must be called 'page' and should NOT be initialized in the script (as it is already initialized by Scout)."
      },
      {
        name: "requirements_txt",
        type: "string",
        required: false,
        description: "Package Requirements. Already installed: playwright, requests, asyncio, standard Python libraries. Specify additional packages in requirements.txt format."
      },
      {
        name: "advanced_browser_settings",
        type: "boolean",
        required: false,
        description: "Enable advanced browser settings for better stealth and ad blocking. Default is false."
      },
      {
        name: "cookie_name",
        type: "string",
        required: false,
        description: "Name of the stored cookie configuration to use for the session"
      }
    ],
    responseExample: {
      id: 1,
      script: "driver.get('https://google.com')\nprint('Navigated to Google')",
      status: "QUEUED",
      result: null,
      session_id: null,
      created_at: "2024-03-15T10:30:00Z",
      updated_at: "2024-03-15T10:30:00Z",
      user_id: 1
    }
  },
  {
    title: "List Custom Scripts",
    endpoint: "/scout/custom-scripts",
    method: "GET",
    description: "List all custom script executions with pagination.",
    authentication: "Bearer token required",
    params: [
      {
        name: "limit",
        type: "integer",
        required: false,
        description: "Maximum number of records to return. Default is 10."
      },
      {
        name: "offset",
        type: "integer",
        required: false,
        description: "Number of records to skip. Default is 0."
      }
    ],
    responseExample: {
      content: [
        {
          id: 1,
          script: "driver.get('https://google.com')",
          status: "COMPLETED",
          result: "Successfully navigated to Google",
          session_id: "session123",
          created_at: "2024-03-15T10:30:00Z",
          updated_at: "2024-03-15T10:35:00Z",
          user_id: 1
        }
      ],
      headers: {
        "X-Total-Count": "1"
      }
    }
  },
  {
    title: "Get Scout Status",
    endpoint: "/scout/{id}",
    method: "GET",
    description: "Retrieves the current status and details of a Scout session.",
    authentication: "Bearer token required",
    params: [
      { 
        name: "id", 
        type: "integer", 
        required: true, 
        description: "The ID of the Scout session to retrieve" 
      }
    ],
    responseExample: {
      id: 2,
      user_prompt: "How do I implement a login system?",
      status: "IN_PROGRESS",
      operator_response: null,
      comments: null,
      created_at: "2024-03-15T10:30:00Z",
      history: [],
      urls: []
    },
    additionalInfo: [
      {
        title: "Status Values",
        items: [
          "QUEUED - Request is queued for processing",
          "IN_PROGRESS - Scout is currently processing the request",
          "ERROR - An error occurred during processing",
          "COMPLETED - Request has been successfully completed",
          "CANCELLED - Request was cancelled by the user"
        ]
      },
      {
        title: "Response Fields",
        items: [
          "operator_response - Contains Scout's response when status is COMPLETED",
          "comments - Contains error messages when status is ERROR",
          "history - Array of historical actions taken by Scout",
          "urls - List of live URLs where Scout's progress can be viewed"
        ]
      }
    ]
  },
  {
    title: "Cancel Scout Session",
    endpoint: "/scout/{id}/cancel",
    method: "PUT",
    description: "Cancels a Scout session.",
    authentication: "Bearer token required",
    params: [
      { 
        name: "id", 
        type: "integer", 
        required: true, 
        description: "The ID of the Scout session to cancel" 
      }
    ],
    responseExample: {
      message: "Scout session cancelled successfully"
    }
  },
  {
    title: "Manage Cookies",
    endpoint: "/scout/cookies",
    method: "POST",
    description: "Store or update cookie configurations for reuse in Scout sessions.",
    authentication: "Bearer token required",
    params: [
      {
        name: "name",
        type: "string",
        required: true,
        description: "Unique name for the cookie configuration"
      },
      {
        name: "cookies",
        type: "array",
        required: true,
        description: "Array of cookie objects in the format exported by browser cookie extensions"
      }
    ],
    responseExample: {
      message: "Successfully stored cookies with name: my-cookies",
      status: "success"
    },
    additionalInfo: [
      {
        title: "Cookie Management",
        items: [
          "Cookies can be exported from browser extensions like EditThisCookie",
          "Stored cookies can be reused in Scout sessions using the cookie_name parameter",
          "If a cookie configuration with the same name exists, it will be updated",
          "Cookie configurations are securely stored and accessible only to your account"
        ]
      }
    ]
  },
  {
    title: "Create 835 Scraper",
    endpoint: "/scout/create_835_scraper",
    method: "POST",
    description: "Creates a new 835 scraper session that automatically logs into healthcare payer portals and downloads 835 files.",
    authentication: "Bearer token required",
    params: [
      {
        name: "payer",
        type: "string",
        required: true,
        description: "The payer portal to scrape. Supported values: ECHO, INSTAMED, PAYSPAN, ZELIS"
      },
      {
        name: "site_id",
        type: "string",
        required: true,
        description: "Site ID for the healthcare facility"
      },
      {
        name: "username",
        type: "string",
        required: true,
        description: "Username for the payer portal login"
      },
      {
        name: "password",
        type: "string",
        required: true,
        description: "Password for the payer portal login"
      },
      {
        name: "corporate_id",
        type: "string",
        required: false,
        description: "Corporate ID for the payer portal (required for some payers)"
      }
    ],
    responseExample: {
      id: 1,
      payer: "INSTAMED",
      site_id: 12345,
      username: "user@example.com",
      password: "password123",
      corporate_id: "CORP001",
      status: "QUEUED",
      result: null,
      created_at: "2024-03-15T10:30:00Z",
      updated_at: "2024-03-15T10:30:00Z"
    },
    additionalInfo: [
      {
        title: "Supported Payer Portals",
        items: [
          "ECHO - Echo Health Ventures portal",
          "INSTAMED - Instamed payment portal",
          "PAYSPAN - Payspan payment portal",
          "ZELIS - Zelis payment portal"
        ]
      },
      {
        title: "Status Values",
        items: [
          "QUEUED - Scraper is queued for processing",
          "IN_PROGRESS - Scraper is currently running",
          "ERROR - An error occurred during processing",
          "COMPLETED - Scraper has successfully completed",
          "CANCELLED - Scraper was cancelled by the user"
        ]
      }
    ]
  },
  {
    title: "List 835 Scrapers",
    endpoint: "/scout/list_835_scrapers",
    method: "GET",
    description: "Retrieves a paginated list of 835 scraper sessions for the authenticated user.",
    authentication: "Bearer token required",
    params: [
      {
        name: "limit",
        type: "integer",
        required: false,
        description: "Maximum number of records to return. Default is 10."
      },
      {
        name: "offset",
        type: "integer",
        required: false,
        description: "Number of records to skip. Default is 0."
      }
    ],
    responseExample: [
      {
        id: 1,
        payer: "INSTAMED",
        site_id: 12345,
        username: "user@example.com",
        password: "password123",
        corporate_id: "CORP001",
        status: "COMPLETED",
        result: "Successfully downloaded 5 835 files",
        created_at: "2024-03-15T10:30:00Z",
        updated_at: "2024-03-15T10:35:00Z"
      },
      {
        id: 2,
        payer: "ZELIS",
        site_id: 67890,
        username: "user2@example.com",
        password: "password456",
        corporate_id: null,
        status: "IN_PROGRESS",
        result: null,
        created_at: "2024-03-15T11:00:00Z",
        updated_at: "2024-03-15T11:00:00Z"
      }
    ],
    additionalInfo: [
      {
        title: "Response Details",
        items: [
          "Returns an array of 835 scraper objects",
          "Each object contains the scraper configuration and current status",
          "Results are sorted by creation date in descending order",
          "The result field contains details about the download process when completed"
        ]
      },
      {
        title: "File Processing",
        items: [
          "Downloaded 835 files are automatically uploaded to the RCM system",
          "Files are processed and transformed for further analysis",
          "Check the result field for processing details and any errors",
          "Files are renamed with a standardized naming convention for tracking"
        ]
      }
    ]
  },
  {
    title: "Prompt Functions",
    description: "These functions can be invoked within a user's prompt to perform specific actions during a Scout session. Each function can be called using the 'Run [function_name] function with [parameters]' syntax in the prompt steps.",
    method: "FUNCTION",
    endpoint: "N/A",
    params: [],
    additionalInfo: [
      {
        title: "two_factor_by_company_mapping",
        items: [
          "Description: Retrieves two-factor authentication messages associated with a specific company mapping ID after a 20-second wait.",
          "Parameters: company_mapping_id (integer)",
          "Example: Step 2. Run two_factor_by_company_mapping function with company_mapping_id 12345."
        ]
      },
      {
        title: "two_factor_by_from_and_to_identity",
        items: [
          "Description: Fetches two-factor messages between specified sender and recipient identities after a 20-second delay.",
          "Parameters: from_identity (string), to_identity (string)",
          "Example: Step 4. Run two_factor_by_from_and_to_identity function with from_identity 'user@example.com' and to_identity 'system@example.com'."
        ]
      },
      {
        title: "latest_two_factor_messages",
        items: [
          "Description: Retrieves the most recent two-factor authentication messages after a 20-second wait.",
          "Parameters: None",
          "Example: Step 6. Run latest_two_factor_messages function."
        ]
      },
      {
        title: "screen_shot_pdf",
        items: [
          "Description: Captures a screenshot of the current page as a PDF and saves it to the specified path.",
          "Parameters: download_file_path (string)",
          "Example: Step 7. Run screen_shot_pdf function with download_file_path '/reports/report.pdf'."
        ]
      },
      {
        title: "fetch_files_list",
        items: [
          "Description: Lists files available in a specified directory and processes any zipped downloads from the session.",
          "Parameters: download_file_path (string)",
          "Example: Step 8. Run fetch_files_list function with download_file_path '/downloads/session_123'."
        ]
      },
      {
        title: "upload_file",
        items: [
          "Description: Uploads a file from the given path to the file input element at the specified index.",
          "Parameters: index (integer), path (string)",
          "Example: Step 9. Run upload_file function with index 0 and path '/files/document.pdf'."
        ]
      },
      {
        title: "click_by_text",
        items: [
          "Description: Clicks on the first element containing the exact text specified.",
          "Parameters: text (string)",
          "Example: Step 3. Run click_by_text function with text 'Submit Form'."
        ]
      },
      {
        title: "download_pdf_file_from_url",
        items: [
          "Description: Downloads a PDF file from the provided URL and saves it to the specified path.",
          "Parameters: url (string), download_file_path (string)",
          "Example: Step 5. Run download_pdf_file_from_url function with url 'https://example.com/report.pdf' and download_file_path '/downloads/report.pdf'."
        ]
      },
      {
        title: "show_all_input_file_elements",
        items: [
          "Description: Makes all file input elements visible on the current page by modifying their CSS styles.",
          "Parameters: None",
          "Example: Step 1. Run show_all_input_file_elements function."
        ]
      },
      {
        title: "configure_retool_header",
        items: [
          "Description: Sets the Origin HTTP header to 'https://athelas.retool.com' for requests, typically used for Retool integrations.",
          "Parameters: None",
          "Example: Step 2. Run configure_retool_header function."
        ]
      },
      {
        title: "select_all_claim_lines_novitas",
        items: [
          "Description: Selects all claim lines checkboxes and clicks the 'Next' button on Novitas's claim processing page.",
          "Parameters: None",
          "Example: Step 4. Run select_all_claim_lines_novitas function."
        ]
      },
      {
        title: "submit_appeal_novitas",
        items: [
          "Description: Clicks the 'Submit' button to finalize appeal submission on Novitas's platform.",
          "Parameters: None",
          "Example: Step 6. Run submit_appeal_novitas function."
        ]
      },
      {
        title: "force_scroll_middle_of_page",
        items: [
          "Description: Scrolls to the middle of the current page viewport and scrolls down 300 pixels.",
          "Parameters: None",
          "Example: Step 3. Run force_scroll_middle_of_page function."
        ]
      },
      {
        title: "scroll_within_element",
        items: [
          "Description: Scrolls within a specific page element identified by its index, with an optional scroll amount (default 100 pixels).",
          "Parameters: element_index (integer), amount (integer, optional)",
          "Example: Step 5. Run scroll_within_element function with element_index 2 and amount 200."
        ]
      },
      {
        title: "click_element_by_class_name",
        items: [
          "Description: Clicks the first element with the specified CSS class name.",
          "Parameters: class_name (string)",
          "Example: Step 7. Run click_element_by_class_name function with class_name 'btn-submit'."
        ]
      },
      {
        title: "print_pdf",
        items: [
          "Description: Generates a PDF from the current page using print emulation and saves it to the specified path.",
          "Parameters: download_file_path (string)",
          "Example: Step 10. Run print_pdf function with the file name as 'output.pdf'."
        ]
      },
      {
        title: "send_keys_one_by_one",
        items: [
          "Description: Simulates typing the specified text character by character into the focused element.",
          "Parameters: text (string)",
          "Example: Step 2. Run send_keys_one_by_one function with text 'username123'."
        ]
      },
      {
        title: "wait_one_second",
        items: [
          "Description: Pauses execution for 1 second to allow for delays between actions.",
          "Parameters: None",
          "Example: Step 1. Run wait_one_second function."
        ]
      },
      {
        title: "save_variable",
        items: [
          "Description: Stores a variable in the operator chat session for later use, with optional appending to existing values.",
          "Parameters: operator_chat_id (integer), variable_name (string), variable_value (string), append_to_variable (boolean, optional)",
          "Example: Step 4. Run save_variable function with operator_chat_id 456, variable_name 'user_id', variable_value '789'."
        ]
      },
      {
        title: "pdf_parse_with_prompt",
        items: [
          "Description: Parses the pdf at the given path according to the given prompt.",
          "Parameters: pdf_path (string), prompt(string)",
          "Example: You need to call fetch_files_list to get the latest file downloads available. Call the function pdf_parse_with_prompt to 'get the approval date.'"
        ]
      }
    ]
  }
];

/* Commented out Release History
const releaseHistory = [
  {
    date: "Mar 3, 2024",
    title: "Stop Scout Mid-Run",
    description: "Ability to stop Scout in the middle of a run."
  },
  {
    date: "Feb 27, 2024",
    title: "Enhanced File Operations",
    description: "Downloading files and screen capturing from sites is now available as function calls"
  },
  {
    date: "Feb 24, 2024",
    title: "Document Upload Integration",
    description: "Scout has the ability to upload documents to Athelas Insights"
  },
  {
    date: "Feb 20, 2024",
    title: "Two-Factor Authentication",
    description: "Two factor authentication using Scout is up and working"
  },
  {
    date: "Feb 18, 2024",
    title: "Initial Launch",
    description: "First Launch of Commure Scout"
  }
];

const ReleaseHistory = () => (
  <div>
    <div className="max-w-4xl mx-auto p-10">
      <div className="mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Release History</h1>
        <p className="text-lg text-gray-600">
          Track the evolution of our API with our release timeline.
        </p>
      </div>
      <div className="space-y-8">
        {releaseHistory.map((release, index) => (
          <div key={index} className="relative">
            {index !== releaseHistory.length - 1 && (
              <div className="absolute left-[1.05rem] top-[2.15rem] w-0.5 h-[calc(100%-1rem)] bg-blue-200"></div>
            )}
            <div className="flex items-start gap-6">
              <div className="relative z-10 mt-1.5 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center shadow-lg">
                <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
              </div>
              <Card className="flex-grow p-6 transform transition-all hover:scale-[1.02] hover:shadow-md">
                <div className="text-sm font-medium text-blue-600 mb-2">{release.date}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{release.title}</h3>
                <p className="text-gray-600">{release.description}</p>
              </Card>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);
*/

const Sidebar = ({ isAuthenticated }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [isApiDocsOpen, setIsApiDocsOpen] = useState(
    location.pathname.startsWith('/docs')
  );

  return (
    <div className="hidden sm:fixed sm:top-0 sm:left-0 sm:w-64 sm:h-full sm:border-r sm:border-gray-200 sm:bg-white sm:z-10 sm:block">
      <div className="flex flex-col items-center justify-center h-24 border-b border-gray-200">
        <img src={CommureLogo} alt="Commure Logo" className="w-32" />
      </div>
      <nav className="flex flex-col h-[calc(100%-6rem)] justify-between">
        <ul className="space-y-2 p-4">
          {isAuthenticated && (
            <>
              <li>
                <div className="flex flex-col">
                  <button
                    onClick={() => setIsApiDocsOpen(!isApiDocsOpen)}
                    className="flex items-center justify-between py-2 px-4 rounded hover:bg-gray-100 w-full"
                  >
                    <span>API Documentation</span>
                    <svg
                      className={`w-4 h-4 transform transition-transform ${
                        isApiDocsOpen ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isApiDocsOpen && (
                    <div className="ml-4 mt-1 space-y-1">
                      <Link
                        to="/docs"
                        className={`block py-1 px-4 rounded hover:bg-gray-100 text-sm ${
                          location.pathname === '/docs' ? 'bg-gray-100' : ''
                        }`}
                      >
                        Overview
                      </Link>
                      <Link
                        to="/docs/prompt-functions"
                        className={`block py-1 px-4 rounded hover:bg-gray-100 text-sm ${
                          location.pathname === '/docs/prompt-functions' ? 'bg-gray-100' : ''
                        }`}
                      >
                        Prompt Functions
                      </Link>
                    </div>
                  )}
                </div>
              </li>
              <li>
                <Link
                  to="/try-it"
                  className="block py-2 px-4 rounded hover:bg-gray-100"
                >
                  Try It Out
                </Link>
              </li>
              <li>
                <Link
                  to="/mfa"
                  className="block py-2 px-4 rounded hover:bg-gray-100"
                >
                  MFA Authenticator
                </Link>
              </li>
              <li>
                <Link
                  to="/cookies"
                  className="block py-2 px-4 rounded hover:bg-gray-100"
                >
                  Manage Cookies
                </Link>
              </li>
              <li>
                <Link
                  to="/triggers"
                  className={`block py-2 px-4 rounded hover:bg-gray-100 ${
                    location.pathname === '/triggers' ? 'bg-gray-100' : ''
                  }`}
                >
                  Scheduler Triggers
                </Link>
              </li>
            </>
          )}
        </ul>
        <div className="mt-auto">
          {isAuthenticated ? (
            <div className="px-4 py-3 border-t border-gray-200">
              <Link to="/dashboard" className="block mb-3">
                <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors">
                  {user?.profile_picture ? (
                    <img 
                      src={user.profile_picture} 
                      alt="Profile" 
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      View API Keys
                    </p>
                  </div>
                </div>
              </Link>
              <LogoutButton />
            </div>
          ) : (
            <div className="px-4 py-3 border-t border-gray-200">
              <Link
                to="/login"
                className="block w-full py-2 px-4 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-center"
              >
                Sign In
              </Link>
            </div>
          )}
        </div>
      </nav>
    </div>
  );
};

const MobileHeader = ({ isAuthenticated }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  if (!isAuthenticated) return null;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="sm:hidden bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex justify-between items-center">
        <img src={CommureLogo} alt="Commure Logo" className="w-24" />
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
      
      {isMobileMenuOpen && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="space-y-2">
            <Link
              to="/try-it"
              className="block py-2 px-3 rounded text-gray-700 hover:bg-gray-100"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Try It Out
            </Link>
            <Link
              to="/mfa"
              className="block py-2 px-3 rounded text-gray-700 hover:bg-gray-100"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              MFA Authenticator
            </Link>
            <Link
              to="/cookies"
              className="block py-2 px-3 rounded text-gray-700 hover:bg-gray-100"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Manage Cookies
            </Link>
            <Link
              to="/triggers"
              className="block py-2 px-3 rounded text-gray-700 hover:bg-gray-100"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Scheduler Triggers
            </Link>
            <Link
              to="/dashboard"
              className="block py-2 px-3 rounded text-gray-700 hover:bg-gray-100"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Dashboard
            </Link>
            <button
              onClick={handleLogout}
              className="block w-full text-left py-2 px-3 rounded text-gray-700 hover:bg-gray-100"
            >
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AppContent = () => {
  const { isAuthenticated } = useAuth();
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar isAuthenticated={isAuthenticated} />
      <MobileHeader isAuthenticated={isAuthenticated} />
      <div className="pl-0 sm:pl-64">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/try-it" element={
            <ProtectedRoute>
              <TryItOut />
            </ProtectedRoute>
          } />
          <Route path="/mfa" element={
            <ProtectedRoute>
              <MfaAuthenticator />
            </ProtectedRoute>
          } />
          <Route path="/cookies" element={
            <ProtectedRoute>
              <CookieManager />
            </ProtectedRoute>
          } />
          <Route path="/triggers" element={
            <ProtectedRoute>
              <SchedulerTriggers />
            </ProtectedRoute>
          } />
          <Route path="/replay/:sessionId" element={
            <ProtectedRoute>
              <ReplayRecording />
            </ProtectedRoute>
          } />
          <Route path="/view-recordings" element={
            <ProtectedRoute>
              <ViewRecordings />
            </ProtectedRoute>
          } />
          <Route path="/" element={
            <ProtectedRoute>
              <MainDocumentation />
            </ProtectedRoute>
          } />
          {/* Commented out Release History Route
          <Route path="/releases" element={<ReleaseHistory />} />
          */}
          <Route path="/docs" element={
            <ProtectedRoute>
              <MainDocumentation />
            </ProtectedRoute>
          } />
          <Route path="/docs/prompt-functions" element={
            <ProtectedRoute>
              <PromptFunctionsDoc />
            </ProtectedRoute>
          } />
        </Routes>
      </div>
    </div>
  );
};

const PromptFunctionsDoc = () => {
  const promptFunctions = apiDocs.find(doc => doc.title === 'Prompt Functions');
  
  return (
    <div className="max-w-4xl mx-auto p-10">
      <div className="mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Prompt Functions</h1>
        <p className="text-lg text-gray-600">
          {promptFunctions.description}
        </p>
      </div>
      
      <div className="space-y-6">
        {promptFunctions.additionalInfo.map((func, index) => (
          <Card key={index}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">{func.title}</h2>
                <MethodBadge method="FUNCTION" />
              </div>
              <ul className="list-disc pl-5 space-y-2">
                {func.items.map((item, i) => (
                  <li key={i} className="text-gray-600">{item}</li>
                ))}
              </ul>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

const Home = ({ onSelectDoc, docs }) => {
  return (
    <div className="max-w-4xl mx-auto p-10">
      <div className="mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">API Reference</h1>
        <p className="text-lg text-gray-600">
          Welcome to our API documentation. Use these APIs to integrate our services into your application.
        </p>
      </div>
      <div className="space-y-6">
        {docs.map((doc, index) => (
          <Card key={index}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">{doc.title}</h2>
                <MethodBadge method={doc.method} />
              </div>
              <p className="text-gray-600 mb-4">{doc.description}</p>
              <div className="bg-gray-50 rounded-md p-4 font-mono text-sm">
                {doc.endpoint}
              </div>
              <Button className="mt-4" onClick={() => onSelectDoc(index)}>
                View Details
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

const DocDetail = ({ doc, onBack }) => {
  return (
    <div>
      <div className="max-w-4xl mx-auto p-10">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <Button onClick={onBack} className="!bg-gray-500 hover:!bg-gray-600">
              ← Back
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">{doc.title}</h1>
            <MethodBadge method={doc.method} />
          </div>
          <p className="text-lg text-gray-600">{doc.description}</p>
          {doc.authentication && (
            <div className="mt-2 inline-block px-3 py-1 bg-yellow-100 text-yellow-800 rounded-md text-sm font-medium">
              {doc.authentication}
            </div>
          )}
        </div>

        <Card className="mb-8">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Endpoint</h3>
            <div className="bg-gray-50 rounded-md p-4 font-mono text-sm">
              {doc.endpoint}
            </div>
          </div>
        </Card>

        <Card className="mb-8">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Parameters</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-gray-600">Parameter</th>
                    <th className="text-left py-3 px-4 text-gray-600">Type</th>
                    <th className="text-left py-3 px-4 text-gray-600">Required</th>
                    <th className="text-left py-3 px-4 text-gray-600">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {doc.params.map((param, i) => (
                    <tr key={i} className="border-b border-gray-200">
                      <td className="py-3 px-4 font-mono text-sm">{param.name}</td>
                      <td className="py-3 px-4">
                        <span className="inline-block px-2 py-1 bg-gray-100 rounded text-sm">
                          {param.type}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {param.required ? (
                          <span className="text-red-600">Required</span>
                        ) : (
                          <span className="text-gray-500">Optional</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {param.description || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>

        {doc.responseExample && (
          <Card className="mb-8">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Example Response</h3>
              <div className="bg-gray-50 rounded-md p-4 font-mono text-sm whitespace-pre">
                {JSON.stringify(doc.responseExample, null, 2)}
              </div>
            </div>
          </Card>
        )}

        {doc.additionalInfo && (
          <div className="space-y-6">
            {doc.additionalInfo.map((info, index) => (
              <Card key={index}>
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-4">{info.title}</h3>
                  <ul className="list-disc pl-5 space-y-2">
                    {info.items.map((item, i) => (
                      <li key={i} className="text-gray-600">{item}</li>
                    ))}
                  </ul>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const LogoutButton = () => {
  const { logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <button
      onClick={handleLogout}
      className="w-full py-2 px-4 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
    >
      Sign Out
    </button>
  );
};

const MainDocumentation = () => {
  const [selectedDoc, setSelectedDoc] = useState(null);
  const filteredDocs = apiDocs.filter(doc => doc.title !== 'Prompt Functions');

  const handleSelectDoc = (index) => {
    setSelectedDoc(index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {selectedDoc === null ? (
        <Home onSelectDoc={handleSelectDoc} docs={filteredDocs} />
      ) : (
        <DocDetail 
          doc={filteredDocs[selectedDoc]} 
          onBack={() => setSelectedDoc(null)} 
        />
      )}
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;
